import { type SupportedLanguage, useTranslation } from '@hbs-us/ui';
import type { ChangeEvent } from 'react';

import { setLanguage } from '../i18n.js';

/**
 * EN ⇄ ES switcher (I18N-05 + A11Y-01). A keyboard-operable native <select>
 * with an accessible label, reflecting the current language. On change it sets
 * the active i18next language AND `<html lang>` (so assistive tech announces
 * the right language). The choice persists for the session via i18next.
 */
const LANGS: SupportedLanguage[] = ['en', 'es'];

export function LanguageSwitcher() {
  const { t, i18n } = useTranslation();
  const current = (i18n.resolvedLanguage ?? 'en') as SupportedLanguage;

  const onChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setLanguage(e.target.value as SupportedLanguage);
  };

  return (
    <label className="inline-flex items-center gap-2 text-sm text-muted-foreground">
      <span className="sr-only">{t('nav:language.label')}</span>
      <select
        aria-label={t('nav:language.label')}
        value={current}
        onChange={onChange}
        /*
          `bg-background`, not `bg-card`: in the dark theme `--card` and `--popover`
          are the same value, so inside the account flyout the field dissolved into
          the panel behind it and only its border survived. `--background` is a step
          off every surface this control is mounted on — the popover, the sidebar
          footer — in both themes. And the ring gap must name a colour: Tailwind v4
          initialises `--tw-ring-offset-color` to `#fff`, so a bare `ring-offset-2`
          drew a white halo here on a dark panel.
        */
        className="h-11 rounded-md border border-border bg-background px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
      >
        {LANGS.map((lang) => (
          <option key={lang} value={lang}>
            {t(`nav:language.${lang}`)}
          </option>
        ))}
      </select>
    </label>
  );
}
